import {
  CraAttributeName,
  FacilityType,
  FacilityWidth,
  FlowDirectionType,
  HeavyVehicleFlowType,
  NumberOfLanesType,
  RiskFactor,
  Score,
} from "@keita/lanepatrol-sdk";
import { t } from "i18next";
import { tRiskFactor } from "../../coding/utils/score-utils";
import { CraAttributeUtils } from "../../coding/utils/attribute-utils";
import { HlaWayType } from "../../hla/models/HlaWay";
import { PresenceDistance } from "../../hla/models/HlaLane";
import {
  booleanOptions,
  generateCraOptions,
  SelectOption,
} from "../../hla/views/HlaWayDetail/form/utils";

export interface NumberAttributeFilter {
  type: CraAttributeFilterType.Number;
  attributeName: CraAttributeName;
  max: number | null;
  min: number | null;
}

export interface TextAreaAttributeFilter {
  type: CraAttributeFilterType.TextArea;
  attributeName: CraAttributeName;
  value: boolean;
}

export interface SelectAttributeFilter {
  type: CraAttributeFilterType.Select;
  attributeName: CraAttributeName;
  value: string;
}

export type CraAttributeFilter =
  | NumberAttributeFilter
  | TextAreaAttributeFilter
  | SelectAttributeFilter;

export enum CraAttributeFilterType {
  Number = "Number",
  TextArea = "TextArea",
  Select = "Select",
}

export enum ScoreFilterType {
  RiskFactors = "RiskFactors",
  Limits = "Limits",
}

export type ScoreFilter = ScoreRiskFactorsFilter | ScoreLimitsFilter;

interface ScoreRiskFactorsFilter {
  type: ScoreFilterType.RiskFactors;
  riskFactors: RiskFactor[];
}

interface ScoreLimitsFilter {
  type: ScoreFilterType.Limits;
  limits: ResultsScoreLimits;
}

export interface ResultsScoreLimits {
  min: number;
  max: number;
}

export enum AttributeSorting {
  Alphabetic = "Alphabetic",
  ScoreImpact = "ScoreImpact",
}

export type ScoreFilters = {
  [key in keyof Score]?: ScoreFilter;
};

export type MinMaxFilter = { min: number | null; max: number | null };

export type HlaAttributeFilter = {
  type: HlaWayType[];
  heavyVehicleFlow: HeavyVehicleFlowType[];
  averageRoadSpeed: MinMaxFilter;
  averageDailyTraffic: MinMaxFilter;
  lit: boolean[];
  flowDirection: FlowDirectionType[];
  facilityType: FacilityType[];
  facilityWidthPerDirection: FacilityWidth[];
  lightSegregation: boolean[];
  tramOrTrainRails: boolean[];

  roadLane: (PresenceDistance | null)[];
  vehicleParking: (PresenceDistance | null)[];
  severeHazard: (PresenceDistance | null)[];
  objectOrLevelChange: (PresenceDistance | null)[];
  sidewalk: (PresenceDistance | null)[];
  numberOfLanes: (NumberOfLanesType | null)[];
  hardSegregation: boolean[];
};

export const emptyHlaAttributeFilter = (): HlaAttributeFilter => ({
  type: [],
  heavyVehicleFlow: [],
  averageRoadSpeed: { min: null, max: null },
  averageDailyTraffic: { min: null, max: null },
  lit: [],
  flowDirection: [],
  facilityType: [],
  facilityWidthPerDirection: [],
  lightSegregation: [],
  tramOrTrainRails: [],

  roadLane: [],
  vehicleParking: [],
  severeHazard: [],
  objectOrLevelChange: [],
  sidewalk: [],
  numberOfLanes: [],
  hardSegregation: [],
});

export const isScoreFiltersEmpty = (scoreFilters: ScoreFilters) => {
  return (
    !scoreFilters.global &&
    !scoreFilters.bicycleToBicycle &&
    !scoreFilters.bicycleToPedestrian &&
    !scoreFilters.singleBicycle &&
    !scoreFilters.vehicleToBicycle
  );
};

export const isHlaAttributeFiltersEmpty = (filters: HlaAttributeFilter) => {
  return (
    filters.type.length === 0 &&
    filters.heavyVehicleFlow.length === 0 &&
    filters.averageRoadSpeed.min == null &&
    filters.averageRoadSpeed.max == null &&
    filters.averageDailyTraffic.min == null &&
    filters.averageDailyTraffic.max == null &&
    filters.lit.length === 0 &&
    filters.flowDirection.length === 0 &&
    filters.facilityType.length === 0 &&
    filters.facilityWidthPerDirection.length === 0 &&
    filters.lightSegregation.length === 0 &&
    filters.tramOrTrainRails.length === 0 &&
    filters.roadLane.length === 0 &&
    filters.vehicleParking.length === 0 &&
    filters.severeHazard.length === 0 &&
    filters.objectOrLevelChange.length === 0 &&
    filters.sidewalk.length === 0 &&
    filters.numberOfLanes.length === 0 &&
    filters.hardSegregation.length === 0
  );
};

export const scoreFilterToString = (filter: ScoreFilter): string => {
  if (filter.type === ScoreFilterType.RiskFactors) {
    return filter.riskFactors.map((rf) => tRiskFactor(rf)).join(", ");
  }
  return t(`map:pieChart.filters.minMax`, {
    min: filter.limits.min.toFixed(2),
    max: filter.limits.max.toFixed(2),
  });
};

export const craAttributeFilterToString = (
  filter: CraAttributeFilter,
): string => {
  switch (filter.type) {
    case CraAttributeFilterType.Number: {
      return `${t(`map:pieChart.filters.minMax`, {
        min: filter.min || 0,
        max: filter.max || "∞",
      })} km/h`;
    }

    case CraAttributeFilterType.TextArea: {
      return filter.value
        ? t(`map:pieChart.textArea.filled`)
        : t(`map:pieChart.textArea.notFilled`);
    }

    case CraAttributeFilterType.Select: {
      return CraAttributeUtils.getOptionLabel(filter.value);
    }
  }
};

export const hlaAttributeFiltersToString = (filters: HlaAttributeFilter) => {
  const labelAndValues: Record<string, string> = {};

  const presenceDistanceOptions = [
    { value: null, label: "Not present" },
    { value: PresenceDistance.From0to1m, label: "0 to 1m" },
    { value: PresenceDistance.From1to3m, label: "1 to 3m" },
  ];

  if (filters.type.length) {
    labelAndValues.type = filters.type
      .map((value) => t(`shared:hlaWayType.${value}`))
      .join(", ");
  }

  if (filters.heavyVehicleFlow.length) {
    labelAndValues.heavyVehicleFlow = filters.heavyVehicleFlow
      .map((value) =>
        getSelectOptionLabel(
          value,
          generateCraOptions(Object.values(HeavyVehicleFlowType)),
        ),
      )
      .join(", ");
  }

  if (filters.averageRoadSpeed.min || filters.averageRoadSpeed.max) {
    labelAndValues.averageRoadSpeed = `From ${
      filters.averageRoadSpeed.min ?? "0"
    } to ${filters.averageRoadSpeed.max ?? "∞"}`;
  }

  if (filters.averageDailyTraffic.min || filters.averageDailyTraffic.max) {
    labelAndValues.averageDailyTraffic = `From ${
      filters.averageDailyTraffic.min ?? "0"
    } to ${filters.averageDailyTraffic.max ?? "∞"}`;
  }

  if (filters.lit.length) {
    labelAndValues.lit = filters.lit
      .map((value) => getSelectOptionLabel(value, booleanOptions))
      .join(", ");
  }

  if (filters.facilityType.length) {
    labelAndValues.facilityType = filters.facilityType
      .map((value) =>
        getSelectOptionLabel(
          value,
          generateCraOptions(Object.values(FacilityType)),
        ),
      )
      .join(", ");
  }

  if (filters.flowDirection.length) {
    labelAndValues.flowDirection = filters.flowDirection
      .map((value) =>
        getSelectOptionLabel(
          value,
          generateCraOptions(Object.values(FlowDirectionType)),
        ),
      )
      .join(", ");
  }

  if (filters.facilityWidthPerDirection.length) {
    labelAndValues.facilityWidthPerDirection = filters.facilityWidthPerDirection
      .map((value) =>
        getSelectOptionLabel(
          value,
          generateCraOptions(Object.values(FacilityWidth)),
        ),
      )
      .join(", ");
  }

  if (filters.tramOrTrainRails.length) {
    labelAndValues.tramOrTrainRails = filters.tramOrTrainRails
      .map((value) => getSelectOptionLabel(value, booleanOptions))
      .join(", ");
  }

  if (filters.lightSegregation.length) {
    labelAndValues.lightSegregation = filters.lightSegregation
      .map((value) => getSelectOptionLabel(value, booleanOptions))
      .join(", ");
  }

  if (filters.hardSegregation.length) {
    labelAndValues.hardSegregation = filters.hardSegregation
      .map((value) => getSelectOptionLabel(value, booleanOptions))
      .join(", ");
  }

  if (filters.roadLane.length) {
    labelAndValues.roadLane = filters.roadLane
      .map((value) => getSelectOptionLabel(value, presenceDistanceOptions))
      .join(", ");
  }

  if (filters.numberOfLanes.length) {
    labelAndValues.numberOfLanes = filters.numberOfLanes
      .map((value) =>
        getSelectOptionLabel(
          value,
          generateCraOptions(Object.values(NumberOfLanesType)),
        ),
      )
      .join(", ");
  }

  if (filters.vehicleParking.length) {
    labelAndValues.vehicleParking = filters.vehicleParking
      .map((value) => getSelectOptionLabel(value, presenceDistanceOptions))
      .join(", ");
  }

  if (filters.sidewalk.length) {
    labelAndValues.sidewalk = filters.sidewalk
      .map((value) => getSelectOptionLabel(value, presenceDistanceOptions))
      .join(", ");
  }

  if (filters.objectOrLevelChange.length) {
    labelAndValues.objectOrLevelChange = filters.objectOrLevelChange
      .map((value) => getSelectOptionLabel(value, presenceDistanceOptions))
      .join(", ");
  }

  if (filters.severeHazard.length) {
    labelAndValues.severeHazard = filters.severeHazard
      .map((value) => getSelectOptionLabel(value, presenceDistanceOptions))
      .join(", ");
  }

  return labelAndValues;
};

const getSelectOptionLabel = (value: any, options: SelectOption[]) => {
  console.log(value, options);
  return options.find((option) => option.value === value)?.label ?? "Unknown";
};
